import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";

import { useWorkingDays } from "../hooks/useWorkingDays";
import {
  type SellerMonthlyGoals,
  computeAnalysisMetrics,
} from "../types/salesGoals";
import MonthlyAnalysisHeader from "./MonthlyAnalysisHeader";

interface SellerAnalysisTableProps {
  sellers: SellerMonthlyGoals[];
  selectedMonth: number;
  year: number;
  isLoading: boolean;
}

function formatCLP(amount: number): string {
  return "$" + Math.round(amount).toLocaleString("es-CL");
}

function formatPct(pct: number): string {
  return `${Math.round(pct)}%`;
}

function pctColor(pct: number): string {
  if (pct >= 80) return "text-emerald-600";
  if (pct >= 50) return "text-orange-500";
  return "text-red-500";
}

export default function SellerAnalysisTable({
  sellers,
  selectedMonth,
  year,
  isLoading,
}: SellerAnalysisTableProps) {
  const wd = useWorkingDays(year, selectedMonth);

  if (isLoading) {
    return <TableSkeleton rows={8} columns={8} />;
  }

  const rows = sellers
    .map((s) => {
      const projection = s.months[selectedMonth] || 0;
      const sales = s.actuals[selectedMonth] || 0;
      const metrics = computeAnalysisMetrics(
        projection,
        sales,
        wd.totalWorkingDays,
        wd.elapsedWorkingDays,
        wd.remainingWorkingDays
      );
      return { ...s, projection, sales, ...metrics };
    })
    .filter((r) => r.projection > 0 || r.sales > 0)
    .sort((a, b) => b.projection - a.projection);

  const totals = rows.reduce(
    (acc, r) => ({
      projection: acc.projection + r.projection,
      sales: acc.sales + r.sales,
      expectedToday: acc.expectedToday + r.expectedToday,
      estimatedClose: acc.estimatedClose + r.estimatedClose,
    }),
    { projection: 0, sales: 0, expectedToday: 0, estimatedClose: 0 }
  );

  const totalPct =
    totals.expectedToday > 0 ? (totals.sales / totals.expectedToday) * 100 : 0;
  const totalEstDaily =
    wd.totalWorkingDays > 0 ? totals.projection / wd.totalWorkingDays : 0;
  const totalActDaily =
    wd.elapsedWorkingDays > 0 ? totals.sales / wd.elapsedWorkingDays : 0;

  return (
    <div className="space-y-4">
      <MonthlyAnalysisHeader
        month={selectedMonth}
        year={year}
        workingDays={wd}
      />

      <div className="w-full min-w-0 overflow-x-auto rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 min-w-[180px] whitespace-nowrap bg-card">
                Vendedor
              </TableHead>
              <TableHead className="min-w-[130px] whitespace-nowrap text-right">
                Proyección
              </TableHead>
              <TableHead className="min-w-[130px] whitespace-nowrap text-right">
                Venta
              </TableHead>
              <TableHead className="min-w-[150px] whitespace-nowrap text-right">
                Debería Llevar Hoy
              </TableHead>
              <TableHead className="min-w-[70px] whitespace-nowrap text-right">
                %
              </TableHead>
              <TableHead className="min-w-[140px] whitespace-nowrap text-right">
                Cierre Estimado
              </TableHead>
              <TableHead className="min-w-[130px] whitespace-nowrap text-right">
                Est. Vta Diaria
              </TableHead>
              <TableHead className="min-w-[130px] whitespace-nowrap text-right">
                Real Vta Diaria
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, index) => (
              <TableRow
                key={r.seller_id || "unassigned"}
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
              >
                <TableCell className="sticky left-0 z-10 bg-card font-medium">
                  {r.seller_name}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCLP(r.projection)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCLP(r.sales)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {wd.elapsedWorkingDays > 0 ? formatCLP(r.expectedToday) : "-"}
                </TableCell>
                <TableCell
                  className={`text-right font-semibold tabular-nums ${
                    wd.elapsedWorkingDays > 0 ? pctColor(r.pctAchievement) : ""
                  }`}
                >
                  {wd.elapsedWorkingDays > 0
                    ? formatPct(r.pctAchievement)
                    : "-"}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCLP(r.estimatedClose)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCLP(r.estimatedDaily)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {wd.elapsedWorkingDays > 0 ? formatCLP(r.actualDaily) : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="sticky left-0 z-10 bg-muted font-bold">
                Total General
              </TableCell>
              <TableCell className="text-right font-bold tabular-nums">
                {formatCLP(totals.projection)}
              </TableCell>
              <TableCell className="text-right font-bold tabular-nums">
                {formatCLP(totals.sales)}
              </TableCell>
              <TableCell className="text-right font-bold tabular-nums">
                {wd.elapsedWorkingDays > 0
                  ? formatCLP(totals.expectedToday)
                  : "-"}
              </TableCell>
              <TableCell
                className={`text-right font-bold tabular-nums ${
                  wd.elapsedWorkingDays > 0 ? pctColor(totalPct) : ""
                }`}
              >
                {wd.elapsedWorkingDays > 0 ? formatPct(totalPct) : "-"}
              </TableCell>
              <TableCell className="text-right font-bold tabular-nums">
                {formatCLP(totals.estimatedClose)}
              </TableCell>
              <TableCell className="text-right font-bold tabular-nums">
                {formatCLP(totalEstDaily)}
              </TableCell>
              <TableCell className="text-right font-bold tabular-nums">
                {wd.elapsedWorkingDays > 0 ? formatCLP(totalActDaily) : "-"}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
}
